import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InPageTabs } from '../../components/InPageTabs';
import { TelemetryFlagsTab } from '../../components/telemetry/TelemetryFlagsTab';
import { createChart, type IChartApi } from 'lightweight-charts';
import {
  listAllVendors,
  getAllVendorCandles,
  applyVendorWriteToAllZones,
  type MergedVendor,
  type ZoneVendorTotal,
  updateVendorRegistry,
  setVendorEnabled,
  setVendorStatus,
  type Vendor,
  type VendorCandle,
} from '../../lib/nexa-config-api';
import { useZoneReadReport } from '../../components/ZoneContext';
import { ZoneFanoutWarning } from '../../components/ZoneIndicators';
import { useStaffMe } from '../../hooks/useStaffMe';
import { ActionConfirmModal } from './ActionConfirmModal';
import { useEffectiveTimeZone, formatInstant, formatClock24 } from '../../lib/time-format';
import { ZoneCell } from '../../components/ZoneIndicators';
import { ActionIcon } from '../../components/ActionIcon';

/**
 * /admin/monitoring — "API Stack Management" (Phase 5d-5 / 5c, ADR-046 §6.4).
 *
 * Vendor API monitoring + management. Two sections:
 *   1. "Call activity" — a 2-column candlestick wall (per-vendor, calls/min,
 *      20-min candles, 24h). ALWAYS shows all vendors (never filtered).
 *      Standard trading convention in blue/silver: silver+hollow = rising load
 *      (close >= open), blue+filled = falling load. Per-vendor amber/red load
 *      threshold lines + panel border. Flat at 0 = outage.
 *   2. "Vendor status & management" — sortable/filterable table (filter + sort
 *      apply to the TABLE only). View → detail panel with disable/enable +
 *      edit-registry (per-vendor thresholds). super_admin edits; nexa_admin views.
 *
 * Candle data comes from GET /admin/config/vendors/{key}/candles (10-min OHLC).
 * The metering emitter is a backend stub at 5c, so when the route returns no
 * buckets we render a clearly-marked SYNTHETIC series for visual review. Real
 * candles flow once the emitter deploys.
 */

const UP = '#cbd5e1'; // silver — rising load
const DOWN = '#3b82f6'; // blue — falling load
const BAND = { good: '#34d399', warn: '#fbbf24', danger: '#f87171', dim: '#64748b' };

type LoadBand = 'good' | 'warn' | 'danger';

/**
 * DEMO seed — exercises the full MED/HIGH/LOW range for UAT while the registry
 * carries no per-vendor thresholds/usage (warnPerMin/dangerPerMin were added to
 * ADR-046 §6.1 after the 5b seed, and the candle metering route is not deployed).
 * Used ONLY as a fallback: a real registry rate (from usage) or real thresholds
 * take precedence. Remove once the registry is seeded + the emitter deploys.
 */
/**
 * ⚠️ DEMO_MODE — REMOVE BEFORE LAUNCH. Tracked: ADR-046 §6.4 deferred list +
 * LAUNCH Build Reference + post-phase-doc-review-notes (6 Jun 2026).
 *
 * Forces the synthetic rates/thresholds below to drive the wall so UAT can see
 * the full LOW/MED/HIGH range while (a) the candle-metering route is undeployed
 * and (b) the registry carries no per-vendor thresholds. This is a REVIEW AID,
 * not production behaviour — with it on, the wall shows fabricated load, not
 * real vendor traffic. Set to false (and delete DEMO_SEED + this flag) once the
 * 10-min OHLC emitter deploys and the registry is seeded with warn/dangerPerMin.
 * Flipping this is a mandatory item on the LAUNCH-stack deploy checklist.
 */
const DEMO_MODE = true;

const DEMO_SEED: Record<string, { rate: number; warn: number; danger: number }> = {
  serper: { rate: 13, warn: 8, danger: 12 }, // HIGH (red) — sustained spike
  openai: { rate: 7, warn: 6, danger: 12 }, // MEDIUM (amber)
  tinybird: { rate: 4, warn: 25, danger: 50 },
  anthropic: { rate: 7, warn: 15, danger: 30 },
  dataforseo: { rate: 2, warn: 4, danger: 8 },
  'google-places': { rate: 4, warn: 5, danger: 10 },
  googleplaces: { rate: 4, warn: 5, danger: 10 },
  xai: { rate: 3, warn: 15, danger: 30 },
};
function demoSeed(v: Vendor) {
  return DEMO_SEED[v.key.toLowerCase()];
}

function vendorRate(v: Vendor): number {
  const d = demoSeed(v);
  if (DEMO_MODE && d) return d.rate; // review aid — overrides real low traffic
  const last = v.usage30d[v.usage30d.length - 1];
  if (last && last.calls > 0) return Math.max(0, Math.round(last.calls / (24 * 60)));
  if (d) return d.rate;
  return v.warnPerMin ? Math.round((v.warnPerMin as number) * 0.6) : 4;
}

function thresholds(v: Vendor): { warn: number; danger: number } {
  const d = demoSeed(v);
  if (DEMO_MODE && d) return { warn: d.warn, danger: d.danger };
  if (v.warnPerMin != null && v.dangerPerMin != null) {
    return { warn: v.warnPerMin as number, danger: v.dangerPerMin as number };
  }
  if (d) return { warn: d.warn, danger: d.danger };
  return { warn: 5, danger: 10 };
}

function bandOf(v: Vendor, rate: number): LoadBand {
  const { warn, danger } = thresholds(v);
  return rate >= danger ? 'danger' : rate >= warn ? 'warn' : 'good';
}

function isDown(v: Vendor): boolean {
  return v.status === 'down' || v.status === 'red';
}
function isDisabled(v: Vendor): boolean {
  return v.enabled === false || v.status === 'not_contracted' || v.status === 'disabled';
}

function loadLabel(v: Vendor, rate: number): { label: string; color: string } {
  if (isDown(v)) return { label: 'DOWN', color: BAND.danger };
  if (isDisabled(v)) return { label: 'disabled', color: BAND.dim };
  const b = bandOf(v, rate);
  return b === 'danger'
    ? { label: 'HIGH', color: BAND.danger }
    : b === 'warn'
      ? { label: 'MEDIUM', color: BAND.warn }
      : { label: 'LOW', color: BAND.good };
}

/**
 * Vendor → public domain map for favicons. The registry backend does not yet
 * return a `domain` field (added to ADR-046 §6.1 after the 5b seed), so the
 * frontend resolves it by vendor key. When the registry carries `domain`, that
 * wins; this map is the fallback.
 */
const VENDOR_DOMAIN: Record<string, string> = {
  anthropic: 'anthropic.com',
  openai: 'openai.com',
  xai: 'x.ai',
  'google-places': 'developers.google.com',
  googleplaces: 'developers.google.com',
  serper: 'serper.dev',
  dataforseo: 'dataforseo.com',
  tinybird: 'tinybird.co',
  'social-provider': '',
  social: '',
};

function domainFor(v: Vendor): string | null {
  if (v.domain) return v.domain;
  const k = v.key.toLowerCase();
  const d = VENDOR_DOMAIN[k];
  return d || null;
}

function faviconUrl(domain?: string | null): string | null {
  return domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=32` : null;
}

/** SYNTHETIC 10-min OHLC for visual review until the metering emitter deploys. */
function syntheticCandles(v: Vendor, stepMin = 20): VendorCandle[] {
  const rate = vendorRate(v);
  if (isDisabled(v) && rate === 0) return [];
  const n = Math.round((24 * 60) / stepMin);
  const now = Math.floor(Date.now() / (stepMin * 60)) * (stepMin * 60);
  const out: VendorCandle[] = [];
  const downStart = n - Math.round(30 / stepMin);
  for (let i = 0; i < n; i++) {
    const t = now - (n - 1 - i) * stepMin * 60;
    if (isDown(v) && i >= downStart) {
      out.push({ t, open: 0, high: 0, low: 0, close: 0, calls: 0, errors: 1 });
      continue;
    }
    const base = Math.max(1, rate) * (0.75 + 0.4 * Math.abs(Math.sin(i / (n / 16))));
    const open = base * (0.85 + Math.random() * 0.3);
    const close = base * (0.85 + Math.random() * 0.3);
    const high = Math.max(open, close) * (1 + Math.random() * 0.25);
    const low = Math.min(open, close) * (1 - Math.random() * 0.2);
    out.push({
      t,
      open: Math.round(open * 10) / 10,
      high: Math.round(high * 10) / 10,
      low: Math.max(0, Math.round(low * 10) / 10),
      close: Math.round(close * 10) / 10,
      calls: Math.round(base * stepMin),
      errors: 0,
    });
  }
  return out;
}

function sla24h(v: Vendor): string {
  // trailing 24h success fraction from the most recent usage day (proxy).
  const last = v.usage30d[v.usage30d.length - 1];
  if (!last || last.calls === 0) return isDisabled(v) ? '—' : '100%';
  const ok = 1 - last.errors / last.calls;
  return `${(ok * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------

type MonitoringTab = 'api' | 'telemetry-flags';

/**
 * /admin/monitoring — two in-page tabs (27 October 2026): the API stack (everything below,
 * unchanged) and Telemetry flags — owners' corrections to their Live visits rows. `?tab=` deep-links.
 */
export function MonitoringPage() {
  const [params, setParams] = useSearchParams();
  const tab: MonitoringTab = params.get('tab') === 'telemetry-flags' ? 'telemetry-flags' : 'api';
  return (
    <>
      <div className="monitoring-tabs">
        <InPageTabs<MonitoringTab>
          tabs={[{ key: 'api', label: 'API stack' }, { key: 'telemetry-flags', label: 'Telemetry flags' }]}
          active={tab}
          onChange={(k) => setParams(k === 'api' ? {} : { tab: k }, { replace: true })}
          ariaLabel="Monitoring sections"
        />
      </div>
      {tab === 'api' ? <ApiStackManagement /> : (
        <div className="monitoring-page alerts-page">
          <h1>Telemetry flags</h1>
          <TelemetryFlagsTab />
        </div>
      )}
    </>
  );
}

function ApiStackManagement() {
  const meState = useStaffMe();
  // UAT fix: vendor-config write is operational (config:manage_vendor = {nexa_admin, super_admin}).
  const canEdit = meState.status === 'loaded' && (meState.me.role === 'super_admin' || meState.me.role === 'nexa_admin');

  const [vendors, setVendors] = useState<MergedVendor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * ADR-122 Pattern 4 — the aggregate, and the zones that did not contribute to it.
   *
   * ⭐⭐ ON THIS PAGE A MISSING ZONE MAKES THE NUMBERS WRONG, NOT SHORT. Elsewhere in ADR-122
   * an absent zone means missing rows and a shorter list; here it means a spend and call-rate
   * figure that is false while looking entirely normal. So the total is never rendered without
   * knowing whether every zone answered.
   */
  const [totals, setTotals] = useState<{ calls: number; units: number; errors: number } | null>(null);
  const [zoneTotals, setZoneTotals] = useState<ZoneVendorTotal[]>([]);
  const [failedZones, setFailedZones] = useState<string[]>([]);
  const [candlesByKey, setCandlesByKey] = useState<Record<string, VendorCandle[]>>({});
  // ADR-123 Decision 1 — Pattern 4: the navigation bar names the zones summed into the totals.
  // Uncounted: a vendor row is not a per-zone record, so a count would be meaningless here.
  useZoneReadReport({ rows: totals ? [] : null, failedZones, counted: false });

  const [filter, setFilter] = useState<'all' | 'red' | 'amber' | 'green' | 'disabled'>('all');
  const [sortKey, setSortKey] = useState<'name' | 'status' | 'sla' | 'rate'>('status');
  const [sortDir, setSortDir] = useState<1 | -1>(-1);
  const [viewKey, setViewKey] = useState<string | null>(null);
  const [zoomKey, setZoomKey] = useState<string | null>(null);

  /**
   * Per-admin candle-wall layout (ARCHITECTURAL SEAM, ADR-046 §6.4 deferred /
   * LAUNCH stack). The wall renders from an explicit ordered array, and each
   * vendor can be `pinned`. Drag-to-reorder + pin UI and per-admin persistence
   * (a layout store keyed by staffId) are a post-INFRA, self-contained feature
   * PR — see the LAUNCH stack. Until then: order defaults to worst-first (so a
   * problem vendor rises automatically) and pins are an empty in-memory set.
   */
  const [pinned, setPinned] = useState<Set<string>>(new Set());
  // setPinned is the seam the future pin-toggle UI calls; referenced to keep it
  // live without exposing UI yet.
  void setPinned;

  useEffect(() => {
    let alive = true;
    listAllVendors()
      .then(async ({ vendors, totals: t, zoneTotals: zt, failedZones: failed }) => {
        if (!alive) return;
        setVendors(vendors);
        setTotals(t);
        setZoneTotals(zt);
        setFailedZones(failed);
        // fetch candles per vendor; fall back to synthetic on empty/failure
        const entries = await Promise.all(
          vendors.map(async (v) => {
            try {
              const { buckets } = await getAllVendorCandles(v.key);
              return [v.key, buckets.length ? buckets : syntheticCandles(v)] as const;
            } catch {
              return [v.key, syntheticCandles(v)] as const;
            }
          }),
        );
        if (alive) setCandlesByKey(Object.fromEntries(entries));
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, []);

  const counts = useMemo(() => {
    const c = { all: 0, red: 0, amber: 0, green: 0, disabled: 0 };
    (vendors ?? []).forEach((v) => {
      c.all++;
      if (isDisabled(v)) c.disabled++;
      else if (isDown(v)) c.red++;
      else {
        const b = bandOf(v, vendorRate(v));
        if (b === 'danger') c.red++;
        else if (b === 'warn') c.amber++;
        else c.green++;
      }
    });
    return c;
  }, [vendors]);

  /**
   * Candle-wall order (the seam): pinned vendors first (future drag/pin UI),
   * then worst-first by load band so a problem vendor rises automatically —
   * the operational "see the problem at the top" need, met by ordering rather
   * than (deferred) manual drag.
   */
  const wallOrder = useMemo(() => {
    const severity = (v: Vendor) =>
      isDown(v) ? 4 : bandOf(v, vendorRate(v)) === 'danger' ? 3 : bandOf(v, vendorRate(v)) === 'warn' ? 2 : isDisabled(v) ? 0 : 1;
    return (vendors ?? [])
      .slice()
      .sort((a, b) => {
        const pa = pinned.has(a.key) ? 1 : 0;
        const pb = pinned.has(b.key) ? 1 : 0;
        if (pa !== pb) return pb - pa; // pinned first
        return severity(b) - severity(a); // then worst-first
      });
  }, [vendors, pinned]);

  if (error) {
    return (
      <div className="monitoring-page">
        <h1>API Stack Management</h1>
        <div className="error-banner">Couldn’t load vendors: {error}</div>
      </div>
    );
  }
  if (!vendors) {
    return (
      <div className="monitoring-page">
        <h1>API Stack Management</h1>
        <p className="muted">Loading vendors…</p>
      </div>
    );
  }

  const tableVendors = vendors
    .filter((v) => {
      if (filter === 'all') return true;
      if (filter === 'disabled') return isDisabled(v);
      const b = isDown(v) ? 'danger' : bandOf(v, vendorRate(v));
      if (filter === 'red') return b === 'danger';
      if (filter === 'amber') return b === 'warn';
      if (filter === 'green') return b === 'good' && !isDisabled(v);
      return true;
    })
    .slice()
    .sort((a, b) => {
      let r = 0;
      const wt = (v: Vendor) =>
        isDisabled(v) ? -1 : isDown(v) ? 3 : bandOf(v, vendorRate(v)) === 'danger' ? 3 : bandOf(v, vendorRate(v)) === 'warn' ? 2 : 0;
      if (sortKey === 'status') r = wt(b) - wt(a);
      else if (sortKey === 'sla') r = (parseFloat(sla24h(a)) || 0) - (parseFloat(sla24h(b)) || 0);
      else if (sortKey === 'rate') r = vendorRate(a) - vendorRate(b);
      else r = a.displayName.localeCompare(b.displayName);
      return r * sortDir;
    });

  const setSort = (k: typeof sortKey) => {
    if (sortKey === k) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(k);
      setSortDir(k === 'name' ? 1 : -1);
    }
  };

  return (
    <div className="monitoring-page">
      <h1>API Stack Management</h1>
      <p className="crumb">Admin → COG → Monitoring</p>

      {/* ⭐⭐ THE TOTAL IS MARKED INCOMPLETE THE MOMENT A ZONE DROPS OUT. A partial sum
          presented as whole is the specific failure ADR-122 singles this page out for: unlike
          a short list, a wrong number looks exactly like a right one. */}
      <ZoneFanoutWarning
        failedZones={failedZones}
        noun="vendor calls"
        onRetry={() => window.location.reload()}
      />
      {totals && <VendorTotals totals={totals} zoneTotals={zoneTotals} incomplete={failedZones.length > 0} />}

      {/* SECTION 1 — API Call Activity (candle wall, always all vendors) */}
      <div className="mon-seclabel">API Call Activity</div>
      <div className="mon-panelhdr">
        <span className="mon-title">API calls / min per vendor — last 24 hours · 20-min candles · all vendors</span>
        <HelpCircle />
        <span className="mon-legend">
          <span style={{ color: UP }}>● silver/hollow = rising load</span>{' '}
          <span style={{ color: DOWN }}>● blue/filled = falling load</span>
        </span>
      </div>
      <div className="mon-cgrid">
        {wallOrder.map((v) => (
          <VendorCandlePanel
            key={v.key}
            vendor={v}
            candles={candlesByKey[v.key] ?? []}
            canEdit={canEdit}
            onZoom={() => setZoomKey(v.key)}
            onChanged={(updated) =>
              setVendors((vs) => (vs ? vs.map((x) => (x.key === updated.key ? { ...x, ...updated } : x)) : vs))
            }
          />
        ))}
      </div>

      {/* SECTION 2 — Vendor status & management (table; filter+sort here only) */}
      <div className="mon-seclabel mon-seclabel-2">
        Vendor Status &amp; Management{' '}
        <span className="muted-sm">— filter and sort below apply to the table only</span>
      </div>
      <div className="mon-filters">
        <span className="muted-sm">Show:</span>
        {(['all', 'red', 'amber', 'green', 'disabled'] as const).map((f) => (
          <button
            key={f}
            type="button"
            className={`mon-fpill${filter === f ? ' on' : ''} mon-fpill-${f}`}
            onClick={() => setFilter(f)}
          >
            {f[0].toUpperCase() + f.slice(1)} <span className="c">{counts[f]}</span>
          </button>
        ))}
        <span className="mon-perm">super_admin edits · nexa_admin views all</span>
      </div>
      <table className="mon-vtable">
        <thead>
          <tr>
            <SortableTh label="Vendor" k="name" cur={sortKey} dir={sortDir} onSort={setSort} />
            <SortableTh label="Status" k="status" cur={sortKey} dir={sortDir} onSort={setSort} />
            <SortableTh label="24h SLA" k="sla" cur={sortKey} dir={sortDir} onSort={setSort} />
            <SortableTh label="Calls/min" k="rate" cur={sortKey} dir={sortDir} onSort={setSort} />
            <th>Balance</th>
            <th className="mon-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {tableVendors.map((v) => (
            <VendorRow key={v.key} vendor={v} onView={() => setViewKey(v.key)} />
          ))}
        </tbody>
      </table>

      {zoomKey && (
        <ZoomModal
          vendor={vendors.find((v) => v.key === zoomKey)!}
          candles={candlesByKey[zoomKey] ?? []}
          onClose={() => setZoomKey(null)}
          onDetails={() => {
            setZoomKey(null);
            setViewKey(zoomKey);
          }}
        />
      )}
      {viewKey && (
        <VendorDetailPanel
          vendor={vendors.find((v) => v.key === viewKey)!}
          canEdit={canEdit}
          onClose={() => setViewKey(null)}
          onChanged={(updated) =>
            setVendors((vs) => (vs ? vs.map((x) => (x.key === updated.key ? { ...x, ...updated } : x)) : vs))
          }
        />
      )}
    </div>
  );
}

// --- candle panel (wall) ---------------------------------------------------

function VendorCandlePanel({
  vendor,
  candles,
  canEdit,
  onZoom,
  onChanged,
}: {
  vendor: Vendor;
  candles: VendorCandle[];
  canEdit: boolean;
  onZoom: () => void;
  onChanged: (v: Partial<Vendor> & { key: string }) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const tz = useEffectiveTimeZone();
  const rate = vendorRate(vendor);
  const { label, color } = loadLabel(vendor, rate);
  const down = isDown(vendor);
  const disabled = isDisabled(vendor);
  const borderCls = down
    ? 'red'
    : disabled
      ? 'dim'
      : bandOf(vendor, rate) === 'danger'
        ? 'red'
        : bandOf(vendor, rate) === 'warn'
          ? 'amber'
          : '';

  // ── Vendor-outage TOGGLE (batch-2 Stage 1, item #3) — a genuine one-click
  // on/off switch directly under the candle chart. UAT rejected the prior
  // "actions strip" because it still opened a modal-confirm gate; this is the
  // rework. Contract:
  //   • Toggle is one click. Flipping to ACTIVE needs NO modal (fixed audit
  //     reason). There is NO confirm-modal gate on the toggle itself.
  //   • Flipping to DOWN opens a SMALL reason modal (the audit reason stays
  //     mandatory) — captured then applied; not a heavy danger-confirm.
  //   • super_admin only (canEdit). nexa_admin sees the toggle DISABLED
  //     (read-only) — it is shown for all contracted vendors, not hidden.
  // Same backend (setVendorStatus); this is a UI rework of trigger + display.
  const [simConfirmOpen, setSimConfirmOpen] = useState(false);
  const [simReason, setSimReason] = useState('');
  const [simBusy, setSimBusy] = useState(false);
  const [simErr, setSimErr] = useState<string | null>(null);
  const showToggle = vendor.status !== 'not_contracted';

  const confirmSimulateDown = async () => {
    if (!simReason.trim()) {
      setSimErr('A reason is required.');
      return;
    }
    setSimBusy(true);
    setSimErr(null);
    try {
      // ADR-122 Pattern 4 — a vendor-registry write is PLATFORM CONFIG, so it lands in EVERY
      // zone. Marking a vendor down in AU while US keeps calling it is not a per-zone
      // decision, it is a half-applied one: the other zone's report pipeline would go on
      // hammering a vendor the operator believes they have stopped. A partial application is
      // surfaced, never swallowed — that is how two zones diverge unnoticed.
      const { results, failedZones } = await applyVendorWriteToAllZones((z) =>
        setVendorStatus(vendor.key, 'down', simReason, z),
      );
      // Surface the actor/timestamp immediately (the list reload resolves the
      // friendly name; show the timestamp + reason locally meanwhile).
      onChanged({ key: vendor.key, status: 'down', updatedAt: results[0]!.updatedAt, updatedReason: simReason });
      setSimConfirmOpen(false);
      setSimReason('');
      if (failedZones.length) {
        setSimErr(`Applied, but not in ${failedZones.join(' and ')} — those zones are unchanged.`);
      }

    } catch (e) {
      setSimErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSimBusy(false);
    }
  };

  const clearSimulatedDown = async () => {
    setSimBusy(true);
    setSimErr(null);
    try {
      // ADR-122 Pattern 4 — a vendor-registry write is PLATFORM CONFIG, so it lands in EVERY
      // zone. Marking a vendor down in AU while US keeps calling it is not a per-zone
      // decision, it is a half-applied one: the other zone's report pipeline would go on
      // hammering a vendor the operator believes they have stopped. A partial application is
      // surfaced, never swallowed — that is how two zones diverge unnoticed.
      const { results, failedZones } = await applyVendorWriteToAllZones((z) =>
        setVendorStatus(vendor.key, 'active', 'Cleared outage toggle', z),
      );
      onChanged({ key: vendor.key, status: 'active', updatedAt: results[0]!.updatedAt, updatedByName: null, updatedReason: null });
      if (failedZones.length) {
        setSimErr(`Applied, but not in ${failedZones.join(' and ')} — those zones are unchanged.`);
      }

    } catch (e) {
      setSimErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSimBusy(false);
    }
  };

  useEffect(() => {
    if (!ref.current || disabled || candles.length === 0) return;
    const chart = drawCandles(ref.current, vendor, candles, 110, tz);
    chartRef.current = chart;
    return () => {
      chart.remove();
      chartRef.current = null;
    };
  }, [vendor, candles, disabled, tz]);

  const fav = faviconUrl(domainFor(vendor));
  return (
    <div
      className={`mon-cpanel ${borderCls}`}
      onClick={disabled ? undefined : onZoom}
      style={{ cursor: disabled ? 'default' : 'pointer' }}
      title={disabled ? undefined : 'Click to zoom'}
    >
      <div className="mon-ch">
        {fav && <img src={fav} width={24} height={24} alt="" className="mon-fav" />}
        <span className="mon-nm">{vendor.displayName}</span>
        <span className="mon-state" style={{ color }}>
          {label}
          {!down && !disabled ? ' load' : ''}
        </span>
        <span className="mon-now">
          now <b>{down ? '0' : disabled ? '—' : rate}</b>/min {!disabled && <span className="mon-zicon">⤢</span>}
        </span>
      </div>
      {disabled && candles.length === 0 ? (
        <div className="mon-flat">no API calls — not contracted</div>
      ) : (
        <div className="mon-chartmini" ref={ref} />
      )}

      {showToggle && (
        // Clicks here must NOT bubble up to the panel's zoom handler.
        <div className="mon-cpanel-toggle" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            role="switch"
            aria-checked={!down}
            aria-label={`Mark ${vendor.displayName} ${down ? 'active' : 'DOWN'}`}
            className={`mon-switch ${down ? 'is-down' : 'is-active'}`}
            disabled={!canEdit || simBusy}
            onClick={down ? clearSimulatedDown : () => { setSimErr(null); setSimConfirmOpen(true); }}
            title={
              canEdit
                ? down
                  ? 'Toggle back to active (clears the critical alert)'
                  : 'Toggle DOWN — raises a real vendor:api_down critical (asks for a reason)'
                : 'super_admin only'
            }
          >
            <span className="mon-switch-track" aria-hidden="true">
              <span className="mon-switch-thumb" />
            </span>
            <span className="mon-switch-label">{simBusy ? 'Saving…' : down ? 'DOWN' : 'Active'}</span>
          </button>
          {down && (
            <span className="mon-switch-meta">
              Set DOWN
              {vendor.updatedByName ? ` by ${vendor.updatedByName}` : ''}
              {vendor.updatedAt ? ` · ${formatInstant(vendor.updatedAt, tz, 'datetime').text}` : ''}
            </span>
          )}
        </div>
      )}

      {/* Flip-to-DOWN reason capture — a SMALL modal (audit reason is mandatory),
          NOT a heavy danger-confirm. The toggle itself is the one-click control. */}
      <ActionConfirmModal
        open={simConfirmOpen}
        title={`Mark ${vendor.displayName} DOWN`}
        intent="danger"
        primaryLabel="Set DOWN"
        primaryDisabled={!simReason.trim()}
        loading={simBusy}
        error={simErr}
        onConfirm={confirmSimulateDown}
        onCancel={() => {
          if (simBusy) return;
          setSimConfirmOpen(false);
          setSimReason('');
          setSimErr(null);
        }}
      >
        <p style={{ marginTop: 0 }}>
          Raises a real <code>vendor:api_down</code> critical on the alerts surface. Real vendor
          calls aren’t stopped. Toggle back to active to clear it.
        </p>
        <label className="mon-reason">
          Reason (required — audit trail)
          <input
            value={simReason}
            onChange={(e) => setSimReason(e.target.value)}
            placeholder="Why is this vendor being marked down?"
            /* Sole input of an ActionConfirmModal (role="dialog"
               aria-modal="true") opened by an explicit operator action.
               Focus-on-open is the dialog pattern. */
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </label>
      </ActionConfirmModal>
    </div>
  );
}

function drawCandles(el: HTMLElement, vendor: Vendor, candles: VendorCandle[], height: number, tz: string, withLines = false): IChartApi {
  // lightweight-charts renders numeric (epoch) times in UTC by default. Format
  // ticks + crosshair in the viewer's effective timezone (profile preference →
  // device zone) so chart clock matches the labelled instants elsewhere.
  const fmtTime = (sec: number) => formatClock24(sec * 1000, tz);
  const chart = createChart(el, {
    width: el.clientWidth,
    height,
    layout: { background: { color: 'transparent' }, textColor: '#64748b', fontSize: 10, attributionLogo: false },
    grid: { vertLines: { visible: false }, horzLines: { color: 'rgba(148,163,184,0.06)' } },
    rightPriceScale: { borderVisible: false },
    timeScale: {
      borderVisible: false,
      timeVisible: withLines,
      secondsVisible: false,
      tickMarkFormatter: (time: number) => fmtTime(time),
    },
    localization: { timeFormatter: (time: number) => fmtTime(time) },
    handleScroll: false,
    handleScale: false,
  });
  const series = chart.addCandlestickSeries({ borderVisible: true });
  series.setData(
    candles.map((d) => {
      const rose = d.close >= d.open;
      const c = rose ? UP : DOWN;
      return {
        time: d.t as never,
        open: d.open,
        high: d.high,
        low: d.low,
        close: d.close,
        color: rose ? 'rgba(0,0,0,0)' : c,
        wickColor: c,
        borderColor: c,
      };
    }),
  );
  const { warn, danger } = thresholds(vendor);
  series.createPriceLine({ price: warn, color: 'rgba(245,158,11,0.5)', lineWidth: 1, lineStyle: 2, axisLabelVisible: withLines, title: withLines ? `warn ${warn}` : '' });
  series.createPriceLine({ price: danger, color: 'rgba(239,68,68,0.55)', lineWidth: 1, lineStyle: 2, axisLabelVisible: withLines, title: withLines ? `danger ${danger}` : '' });
  chart.timeScale().fitContent();
  return chart;
}

// --- table row + sortable header ------------------------------------------

function SortableTh({
  label,
  k,
  cur,
  dir,
  onSort,
}: {
  label: string;
  k: 'name' | 'status' | 'sla' | 'rate';
  cur: string;
  dir: 1 | -1;
  onSort: (k: 'name' | 'status' | 'sla' | 'rate') => void;
}) {
  return (
    <th className="mon-th-sortable" onClick={() => onSort(k)}>
      {label}
      {cur === k ? <span className="mon-arr">{dir > 0 ? '▲' : '▼'}</span> : <span className="mon-sicon">⇅</span>}
    </th>
  );
}

function VendorRow({ vendor, onView }: { vendor: Vendor; onView: () => void }) {
  const rate = vendorRate(vendor);
  const { label, color } = loadLabel(vendor, rate);
  const down = isDown(vendor);
  const disabled = isDisabled(vendor);
  const fav = faviconUrl(domainFor(vendor));
  const slaColor = down ? BAND.danger : bandOf(vendor, rate) === 'danger' ? BAND.danger : bandOf(vendor, rate) === 'warn' ? BAND.warn : undefined;
  return (
    <tr className={down ? 'mon-rowred' : ''}>
      <td>
        <div className="mon-vname-cell">
          {fav && <img src={fav} width={20} height={20} alt="" className="mon-fav" />}
          <div>
            <div className="mon-vn">{vendor.displayName}</div>
            <div className="mon-vc">{vendor.category}</div>
          </div>
        </div>
      </td>
      <td>
        <span className="mon-hd" style={{ background: color }} /> <span style={{ color }}>{label}</span>
      </td>
      {disabled ? (
        <td colSpan={3} className="muted">
          {vendor.status === 'not_contracted' ? 'Not contracted — adapter stubbed' : 'disabled'}
        </td>
      ) : (
        <>
          <td style={slaColor ? { color: slaColor } : undefined}>{sla24h(vendor)}</td>
          <td>{down ? '0' : rate}/min</td>
          <td>
            {vendor.balanceMode ? <span className={`mon-mode mon-mode-${vendor.balanceMode}`}>{vendor.balanceMode}</span> : '—'}
          </td>
        </>
      )}
      <td className="mon-right">
        <ActionIcon act="view" icon="view" label="View" tip={`View ${vendor.displayName} — status, SLA and balance`} onClick={onView} />
      </td>
    </tr>
  );
}

// --- zoom modal ------------------------------------------------------------

function ZoomModal({
  vendor,
  candles,
  onClose,
  onDetails,
}: {
  vendor: Vendor;
  candles: VendorCandle[];
  onClose: () => void;
  onDetails: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const tz = useEffectiveTimeZone();
  useEffect(() => {
    if (!ref.current || candles.length === 0) return;
    const chart = drawCandles(ref.current, vendor, candles, 380, tz, true);
    return () => chart.remove();
  }, [vendor, candles, tz]);
  const rate = vendorRate(vendor);
  const { label, color } = loadLabel(vendor, rate);
  const fav = faviconUrl(domainFor(vendor));
  return (
    <div className="mon-mback" onClick={onClose}>
      <div className="mon-modal mon-modal-wide" onClick={(e) => e.stopPropagation()}>
        <h3 className="mon-modal-h">
          {fav && <img src={fav} width={28} height={28} alt="" className="mon-fav" />} {vendor.displayName}{' '}
          <span style={{ color, fontWeight: 400, fontSize: 13 }}>{label} load</span>
        </h3>
        <div className="muted-sm" style={{ marginTop: -8, marginBottom: 16 }}>
          API calls/min · last 24h · 20-min candles · now <b>{rate}</b>/min · 24h SLA <b>{sla24h(vendor)}</b> ·{' '}
          <span style={{ color: UP }}>silver/hollow = rising load</span> ·{' '}
          <span style={{ color: DOWN }}>blue/filled = falling load</span>
        </div>
        <div ref={ref} style={{ height: 380, width: '100%' }} />
        <div className="mon-foot">
          <button type="button" className="mon-btn" onClick={onClose}>
            Close
          </button>
          <button type="button" className="mon-btn mon-btn-primary" onClick={onDetails}>
            Vendor details →
          </button>
        </div>
      </div>
    </div>
  );
}

// --- help tooltip ----------------------------------------------------------

function HelpCircle() {
  return (
    <span className="mon-qmark" tabIndex={0}>
      ?
      <span className="mon-tip">
        <b>How to read these candles</b>
        <br />
        Each candle = one 20-minute slice of how hard we called this vendor — calls per minute. Higher = busier.
        Candles (not a line) keep brief spikes visible instead of averaging them away.
        <div className="mon-exrow">
          <svg width={34} height={64} viewBox="0 0 34 64" aria-hidden="true">
            <line x1="17" y1="2" x2="17" y2="14" stroke={UP} strokeWidth="1.5" />
            <line x1="17" y1="46" x2="17" y2="62" stroke={UP} strokeWidth="1.5" />
            <rect x="7" y="14" width="20" height="32" fill="none" stroke={UP} strokeWidth="2" />
          </svg>
          <span className="mon-ex">
            <b style={{ color: UP }}>Silver / hollow = rising load</b>
            closed higher than it opened — we were calling this vendor harder by the end of the slice.
          </span>
        </div>
        <div className="mon-exrow">
          <svg width={34} height={64} viewBox="0 0 34 64" aria-hidden="true">
            <line x1="17" y1="2" x2="17" y2="14" stroke={DOWN} strokeWidth="1.5" />
            <line x1="17" y1="46" x2="17" y2="62" stroke={DOWN} strokeWidth="1.5" />
            <rect x="7" y="14" width="20" height="32" fill={DOWN} stroke={DOWN} strokeWidth="2" />
          </svg>
          <span className="mon-ex">
            <b style={{ color: DOWN }}>Blue / filled = falling load</b>
            closed lower than it opened — call rate eased off during the slice.
          </span>
        </div>
        <b>Colour is direction, not good-or-bad.</b> Whether load is a problem is told by the dashed lines:{' '}
        <b style={{ color: BAND.warn }}>amber</b> = warning, <b style={{ color: BAND.danger }}>red</b> = danger.
        Candles riding above the red line = running hot. Each vendor sets its own levels on its registry record. A
        flat line at 0 = outage.
      </span>
    </span>
  );
}

// --- detail panel (View) ---------------------------------------------------

function VendorDetailPanel({
  vendor,
  canEdit,
  onClose,
  onChanged,
}: {
  vendor: Vendor;
  canEdit: boolean;
  onClose: () => void;
  onChanged: (v: Partial<Vendor> & { key: string }) => void;
}) {
  const [mode, setMode] = useState<'view' | 'edit' | 'toggle'>('view');
  const [warn, setWarn] = useState(String(vendor.warnPerMin ?? 5));
  const [danger, setDanger] = useState(String(vendor.dangerPerMin ?? 10));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const rate = vendorRate(vendor);
  const { label, color } = loadLabel(vendor, rate);
  const fav = faviconUrl(domainFor(vendor));
  const disabled = isDisabled(vendor);

  const saveRegistry = async () => {
    if (!reason.trim()) {
      setErr('A reason is required.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // ADR-122 Pattern 4 — platform config, so it lands in EVERY zone (see applyVendorWriteToAllZones).
      const { failedZones } = await applyVendorWriteToAllZones((z) =>
        updateVendorRegistry(vendor.key, { warnPerMin: Number(warn), dangerPerMin: Number(danger) }, reason, z),
      );
      onChanged({ key: vendor.key, warnPerMin: Number(warn), dangerPerMin: Number(danger) });
      if (failedZones.length) setErr(`Applied, but not in ${failedZones.join(' and ')} — those zones are unchanged.`);
      setMode('view');
      setReason('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleEnabled = async (next: boolean) => {
    if (!reason.trim()) {
      setErr('A reason is required.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // ADR-122 Pattern 4 — platform config, so it lands in EVERY zone (see applyVendorWriteToAllZones).
      const { failedZones } = await applyVendorWriteToAllZones((z) =>
        setVendorEnabled(vendor.key, next, reason, z),
      );
      onChanged({ key: vendor.key, enabled: next });
      if (failedZones.length) setErr(`Applied, but not in ${failedZones.join(' and ')} — those zones are unchanged.`);
      setMode('view');
      setReason('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // ── Vendor-down OUTAGE SIMULATE (batch Stage 3) — super_admin flips the
  // registry `status` to `down`/`active` so UAT can raise/clear a real
  // `vendor:api_down` critical through the alert-evaluator without the CLI.
  // Distinct from enable/disable above (that is the pipeline toggle). Flip-to-DOWN
  // is danger-confirmed (ActionConfirmModal); flip-to-ACTIVE clears with no heavy
  // confirm. Manual revert only — no auto-revert timer.
  const simDown = isDown(vendor);
  const [simConfirmOpen, setSimConfirmOpen] = useState(false);
  const [simReason, setSimReason] = useState('');
  const [simBusy, setSimBusy] = useState(false);
  const [simErr, setSimErr] = useState<string | null>(null);

  const confirmSimulateDown = async () => {
    if (!simReason.trim()) {
      setSimErr('A reason is required.');
      return;
    }
    setSimBusy(true);
    setSimErr(null);
    try {
      // ADR-122 Pattern 4 — platform config, so it lands in EVERY zone (see applyVendorWriteToAllZones).
      const { failedZones } = await applyVendorWriteToAllZones((z) =>
        setVendorStatus(vendor.key, 'down', simReason, z),
      );
      onChanged({ key: vendor.key, status: 'down' });
      if (failedZones.length) setSimErr(`Applied, but not in ${failedZones.join(' and ')} — those zones are unchanged.`);
      setSimConfirmOpen(false);
      setSimReason('');
    } catch (e) {
      setSimErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSimBusy(false);
    }
  };

  const clearSimulatedDown = async () => {
    // Flip-to-active: no heavy confirm. A fixed audit reason is recorded.
    setSimBusy(true);
    setErr(null);
    try {
      // ADR-122 Pattern 4 — platform config, so it lands in EVERY zone (see applyVendorWriteToAllZones).
      const { failedZones } = await applyVendorWriteToAllZones((z) =>
        setVendorStatus(vendor.key, 'active', 'Cleared simulated outage', z),
      );
      if (failedZones.length) setSimErr(`Applied, but not in ${failedZones.join(' and ')} — those zones are unchanged.`);
      onChanged({ key: vendor.key, status: 'active' });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSimBusy(false);
    }
  };

  return (
    <div className="mon-mback" onClick={onClose}>
      <div className="mon-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="mon-modal-h">
          {fav && <img src={fav} width={22} height={22} alt="" className="mon-fav" />} {vendor.displayName}
        </h3>
        <div className="muted-sm" style={{ marginTop: -10, marginBottom: 14 }}>
          {vendor.category}
        </div>

        {mode === 'view' && (
          <>
            <div className="mon-detail-grid">
              <Field label="Status">
                <span className="mon-hd" style={{ background: color }} /> <span style={{ color }}>{label}</span>
              </Field>
              <Field label="24h SLA">{sla24h(vendor)}</Field>
              <Field label="Calls/min (now)">{isDown(vendor) ? '0' : disabled ? '—' : rate}</Field>
              <Field label="Balance mode">{vendor.balanceMode || '—'}</Field>
              <Field label="Warn / Danger (calls/min)">
                {vendor.warnPerMin ?? 5} / {vendor.dangerPerMin ?? 10}
              </Field>
              <Field label="Account email">{vendor.accountEmail ?? '—'}</Field>
              <Field label="Secret name">
                <code className="mon-mono">{vendor.secretName ?? `nexa/${vendor.key}-key`}</code>
              </Field>
            </div>
            <div className="mon-actions">
              {!disabled && vendor.status !== 'not_contracted' && (
                <button
                  type="button"
                  className={`mon-btn mon-btn-sm ${vendor.enabled === false ? 'mon-btn-green' : ''}`}
                  disabled={!canEdit}
                  onClick={() => setMode('toggle')}
                  title={canEdit ? undefined : 'super_admin only'}
                >
                  {vendor.enabled === false ? '▶ Enable vendor' : '⏸ Disable vendor'}
                </button>
              )}
              {vendor.status !== 'not_contracted' && (
                <button
                  type="button"
                  className={`mon-btn mon-btn-sm ${simDown ? 'mon-btn-green' : 'mon-btn-red'}`}
                  disabled={!canEdit || simBusy}
                  onClick={simDown ? clearSimulatedDown : () => setSimConfirmOpen(true)}
                  title={canEdit ? 'Simulate a vendor outage to raise/clear a real critical alert (UAT)' : 'super_admin only'}
                >
                  {simBusy ? 'Saving…' : simDown ? '✓ Clear simulated outage' : '⚠ Simulate outage (DOWN)'}
                </button>
              )}
              <button
                type="button"
                className="mon-btn mon-btn-sm mon-btn-primary"
                disabled={!canEdit}
                onClick={() => setMode('edit')}
                title={canEdit ? undefined : 'super_admin only'}
              >
                ✎ Edit registry
              </button>
            </div>
            <div className="muted-sm" style={{ marginTop: 10 }}>
              super_admin edits · nexa_admin views only
            </div>
            <div className="mon-foot">
              <button type="button" className="mon-btn" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}

        {mode === 'edit' && (
          <>
            <div className="mon-edit-2col">
              <label>
                Warning load (calls/min)
                <input type="number" value={warn} onChange={(e) => setWarn(e.target.value)} />
              </label>
              <label>
                Danger load (calls/min)
                <input type="number" value={danger} onChange={(e) => setDanger(e.target.value)} />
              </label>
            </div>
            <div className="muted-sm" style={{ marginTop: -6, marginBottom: 12 }}>
              Per-vendor load bands for the candle colours — each vendor has its own normal call rate.
            </div>
            <label className="mon-reason">
              Reason (required — audit trail)
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this changing?" />
            </label>
            {err && <div className="mon-err">{err}</div>}
            <div className="mon-foot">
              <button type="button" className="mon-btn" onClick={() => setMode('view')}>
                Cancel
              </button>
              <button type="button" className="mon-btn mon-btn-primary" disabled={busy} onClick={saveRegistry}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}

        {mode === 'toggle' && (
          <>
            <div className={vendor.enabled === false ? 'mon-warnbox mon-warnbox-ok' : 'mon-warnbox'}>
              {vendor.enabled === false
                ? `${vendor.displayName} will be included again from the next report run.`
                : `${vendor.displayName} will be skipped on the next report run. Dependent assessments degrade to partial — that section won’t populate until re-enabled.`}
            </div>
            <label className="mon-reason">
              Reason (required)
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why?" />
            </label>
            {err && <div className="mon-err">{err}</div>}
            <div className="mon-foot">
              <button type="button" className="mon-btn" onClick={() => setMode('view')}>
                Cancel
              </button>
              <button
                type="button"
                className={`mon-btn ${vendor.enabled === false ? 'mon-btn-green' : 'mon-btn-red'}`}
                disabled={busy}
                onClick={() => toggleEnabled(vendor.enabled === false)}
              >
                {busy ? 'Saving…' : vendor.enabled === false ? 'Enable vendor' : 'Disable vendor'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Outage-simulate (flip-to-DOWN) danger confirm (batch Stage 3). */}
      <ActionConfirmModal
        open={simConfirmOpen}
        title={`Simulate an outage for ${vendor.displayName}?`}
        intent="danger"
        primaryLabel="Simulate outage"
        primaryDisabled={!simReason.trim()}
        loading={simBusy}
        error={simErr}
        onConfirm={confirmSimulateDown}
        onCancel={() => {
          if (simBusy) return;
          setSimConfirmOpen(false);
          setSimReason('');
          setSimErr(null);
        }}
      >
        <p style={{ marginTop: 0 }}>
          This marks <strong>{vendor.displayName}</strong> as <strong>DOWN</strong> in the vendor
          registry, raising a real <code>vendor:api_down</code> critical on the alerts surface (it
          shows red here too). It does <strong>not</strong> stop real vendor calls — it’s an
          alerting simulation for UAT. Clear it manually when done (no auto-revert).
        </p>
        <label className="mon-reason">
          Reason (required — audit trail)
          <input
            value={simReason}
            onChange={(e) => setSimReason(e.target.value)}
            placeholder="Why simulate this outage?"
          />
        </label>
      </ActionConfirmModal>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mon-field">
      <div className="mon-field-label">{label}</div>
      <div>{children}</div>
    </div>
  );
}

/**
 * ADR-122 Pattern 4 — the aggregate headline, with the per-zone breakdown that produced it.
 *
 * ⭐⭐ THE BREAKDOWN IS NOT DECORATION; IT IS WHAT MAKES THE TOTAL CHECKABLE. A single summed
 * figure is exactly as convincing when it is wrong as when it is right, and this page is the
 * one ADR-122 singles out for that reason: a missing zone here does not shorten a list, it
 * falsifies a number. Showing each zone's contribution beside the sum means an operator — or
 * the test plan's "spot-check the arithmetic against each zone's own figure" step — can
 * verify it without leaving the page.
 *
 * ⚠ `incomplete` CHANGES THE CLAIM, NOT JUST THE STYLING. When a zone did not answer, the
 * headline stops presenting itself as a platform total and says so in its own label. A partial
 * sum rendered under the word "Total" is the defect; a warning banner elsewhere on the page is
 * necessary but does not undo a number that still reads as complete.
 *
 * Renders in single-zone consoles too, where it is simply an honest 30-day summary with one
 * contributing zone — this is new information rather than a multi-zone affordance.
 */
function VendorTotals({
  totals,
  zoneTotals,
  incomplete,
}: {
  totals: { calls: number; units: number; errors: number };
  /** One row per zone that answered, 0 when it has none (Q16) — the headline is their sum. */
  zoneTotals: ZoneVendorTotal[];
  incomplete: boolean;
}) {
  const n = (v: number) => v.toLocaleString();
  const zoneRows = zoneTotals;
  const errorPct = totals.calls > 0 ? ((totals.errors / totals.calls) * 100).toFixed(2) : '0.00';

  return (
    <section className="mon-totals" aria-label="Vendor usage, last 30 days">
      <div className="mon-seclabel">
        Vendor usage · last 30 days
        {incomplete && <span className="mon-totals-incomplete"> · INCOMPLETE</span>}
      </div>
      <div className="mon-totals-head">
        <div className="mon-totals-figure">
          <span className="mon-totals-n">{n(totals.calls)}</span>
          <span className="mon-totals-lab">
            {/* The label itself stops claiming completeness — see the note above. */}
            {incomplete ? 'calls — some zones missing' : 'calls across all zones'}
          </span>
        </div>
        <div className="mon-totals-figure">
          <span className="mon-totals-n">{n(totals.units)}</span>
          <span className="mon-totals-lab">billable units</span>
        </div>
        <div className="mon-totals-figure">
          <span className="mon-totals-n">{n(totals.errors)}</span>
          <span className="mon-totals-lab">errors · {errorPct}%</span>
        </div>
      </div>

      {/* One row per zone, always — including when only one answered, so "which zones went
          into this" is never something the reader has to infer from the absence of a row. */}
      {zoneRows.length > 0 && (
        <table className="mon-totals-table">
          <thead>
            <tr><th scope="col">Zone</th><th scope="col">Calls</th><th scope="col">Units</th><th scope="col">Errors</th></tr>
          </thead>
          <tbody>
            {zoneRows.map((z) => (
              <tr key={z.zoneCode} data-zone-row={z.zoneCode}>
                <td>
                  <ZoneCell zone={z.zoneCode} />
                  {/* Q16 — a zone with no vendor registry cannot report its usage; say so rather
                      than let its 0 read as "no calls". */}
                  {z.registered === 0 && <span className="mon-totals-unregistered"> · no vendors registered — usage not readable</span>}
                </td>
                <td className="mon-mono">{n(z.calls)}</td>
                <td className="mon-mono">{n(z.units)}</td>
                <td className="mon-mono">{n(z.errors)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
